import type { Option } from "@tento-lona/sheets";
import "@tento-ui/components/event-details/event-details-content";
import "./event-icon.ts";

import { nonnull } from "@tento-core/assert";
import type { EventsClient } from "@tento-lona";
import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { Button } from "@tento-ui/components/button/button";
import { EventDetailsContent } from "@tento-ui/components/event-details/event-details-content";
import { EventTime } from "@tento-ui/components/event-details/event-time";
import { Overlay } from "@tento-ui/components/overlay";
import { Round } from "@tento-ui/components/round";
import { Constants } from "@tento-core/constants";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { AngleRightIcon } from "@tento-ui/ui/icons";
import {
  DateTime,
  DateTimeRegion,
  NaiveDate,
  TimezoneRegion,
  Utc,
} from "@tento-chrono";
import { AuthScopedCalendarId } from "@tento-lona";
import { Network } from "@tento-lona/network";
import type { AuthScopedCalendarDisplay } from "@tento-lona";
import { CalendarsClient } from "@tento-lona";
import { type EventCategories, EventCategoryItem } from "@tento-lona";
import { CalendarPreferences } from "@tento-lona";
import { EventFields } from "./event-fields.ts";
import { EventIcon } from "./event-icon.ts";
import { EventWriteRefusal } from "./event-write-refusal.ts";

/**
 * The form that creates a new event: title, time, calendar and category,
 * written through the `EventsClient`.
 */
@component({
  name: "lona-create-event-form",
})
export class CreateEventForm extends LonaWebComponent {
  // @elements
  $content: EventDetailsContent = this.hydrate("content");
  $icon: EventIcon = this.hydrate("icon");
  $properties: EventDetailsContent.FieldContainer<typeof this.fields.$fields> =
    this.hydrate<
      EventDetailsContent.FieldContainer<typeof this.fields.$fields>
    >("properties");

  // @fields
  fields: EventFields = new EventFields();

  #expandedSet: Set<keyof typeof this.fields.$fields> = new Set(
    this.fields.defaultFieldOrder,
  );
  #unexpandedSet: Set<keyof typeof this.fields.$fields> = new Set([
    "$allDay",
    "$description",
    "$tags",
    "$calendar",
    "$recurrence",
  ]);

  // @props
  onDismiss: () => void = Constants.EMPTY_FUNCTION;
  onUpdate: (state: CreateEventForm.LiveUpdate) => void =
    Constants.EMPTY_FUNCTION;
  focusOnConnected: boolean = false;

  // @state
  #dismissing = false;
  #state: Option<EventFields.State> = null;
  #viewerTz: Option<TimezoneRegion> = null;

  // @deps
  #calendars: Option<CalendarsClient> = null;
  #sdkEvents: Option<EventsClient> = null;
  #calendarPreferences: Option<CalendarPreferences> = null;
  #onShowFlightInfo: Option<() => void> = null;

  /** The draft `bind` set (a read before it is a caller bug). */
  get #draft(): EventFields.State {
    return nonnull(this.#state, "CreateEventForm: draft read before bind");
  }

  /** The calendars `bind` set (a read before it is a caller bug). */
  get #boundCalendars(): CalendarsClient {
    return nonnull(this.#calendars, "CreateEventForm: calendars read before bind");
  }

  /**
   * Title
   */
  set eventTitle(s: Option<string>) {
    this.$content.$title.controller.text = s;
    this.#draft.title = s;
  }

  get eventTitle(): Option<string> {
    return this.$content.$title.controller.text ?? null;
  }

  set eventCategory(category: Option<EventCategoryItem>) {
    if (!this.#state) return;
    if (this.fields.setSuggestedCategory(category)) {
      this.$icon.category = this.fields.selectedCategory();
    }
  }

  get eventCategory(): Option<EventCategoryItem> {
    return this.fields.selectedCategory();
  }

  focusTitle() {
    this.$content.$title.controller.focus();
  }

  constructor() {
    super();

    this.$("more-options-icon").innerHTML = AngleRightIcon;
    GESTURE_MANAGER.addPointerEvent(this.$("more-options"), {
      onClick: () => {
        const expanded = this.toggleAttribute("properties-expanded");
        if (expanded) {
          this.$properties.bindVisible(this.#expandedSet);
          this.$("more-options-label").textContent = "Less Options";
        } else {
          this.$properties.bindVisible(this.#unexpandedSet);
          this.$("more-options-label").textContent = "More Options";
        }
      },
    });

    this.fields.bindDelegate({
      calendarWritability: (calendar) => this.#calendarWritability(calendar),
      onUpdate: (state, changedFields) => {
        // EventFields manages the state internally, just sync our reference
        this.#state = state;
        if (changedFields.has("isAllDay")) {
          // Allocate the all-day representation on the first toggle to all-day
          // from the timed range's days; then keep BOTH representations across
          // every toggle (each surface's resize edits only its own), so the
          // toggle is stable. The active one is chosen by `selectedEventTime`.
          if (state.isAllDay && !state.allDayDateRange && this.#viewerTz) {
            state.allDayDateRange = EventFields.timedRangeDays(
              state.time,
              this.#viewerTz,
            );
          }
          // Relocate the ghost preview (timed grid ↔ all-day row) in place —
          // the app-layer onUpdate reads isAllDay and repaints the ghost in the
          // right subsystem. The popover itself is not torn down.
          this.$content.bindTime(EventFields.selectedEventTime(state));
          this.#emitLiveUpdate();
        }
      },
      onShowFlightInfo: () => this.#onShowFlightInfo?.(),
    });
    this.$properties.bind(this.fields.$fields, this.fields.defaultFieldOrder);
    this.$properties.bindVisible(this.#unexpandedSet);
  }

  showAsModal() {
    this.focusOnConnected = true;
    this.onDismiss = () => Overlay.dismiss(this);
    this.assignStyles({
      width: "100%",
    });
    Overlay.reveal(
      Round.makeWithContent(this, Round.Props.MODAL).assignStyles({
        padding: "16px 12px",
        // Overlay.reveal forces width:100% on the revealed element, so the
        // designed 360px must be a max-width to survive and cap the modal.
        maxWidth: "360px",
        height: "min-content",
      }),
    );
  }

  bindDependency(
    calendars: CalendarsClient,
    calendarPreferences: CalendarPreferences,
    onShowFlightInfo: Option<() => void>,
    sdkEvents: EventsClient,
    /** Tells the user a create was refused (the host's calendar recovery); default `EventWriteRefusal.toastForCalendar`. */
    reportCreateRefused: Option<EventWriteRefusal.CalendarReport>,
  ): this {
    this.#calendars = calendars;
    this.#calendarPreferences = calendarPreferences;
    this.#onShowFlightInfo = onShowFlightInfo;
    this.#sdkEvents = sdkEvents;
    this.#reportCreateRefused = reportCreateRefused;
    return this;
  }

  #reportCreateRefused: Option<EventWriteRefusal.CalendarReport> = null;

  /** Whether a calendar takes writes, as the client knows it (the pickers' filter). */
  readonly #calendarWritability = (calendar: AuthScopedCalendarDisplay): Option<EventsClient.Writability> =>
    this.#sdkEvents?.peekCalendarWritability(calendar) ?? null;

  /** The client's event categories (`sdkEvents.categories`), once `bindDependency` ran. */
  get #categories(): EventCategories {
    return nonnull(
      this.#sdkEvents,
      "CreateEventForm: categories read before bindDependency()",
    ).categories;
  }

  #emitLiveUpdate(): void {
    const state = nonnull(
      this.#state,
      "CreateEventForm.emitLiveUpdate() called before bind()",
    );
    const category = this.fields.selectedCategory();
    this.onUpdate({
      title: state.title ?? null,
      time: state.time,
      category,
      backgroundColor: category == null ? null : category.colorOpt() ?? "var(--gray)",
      isAllDay: state.isAllDay,
      allDayDateRange: state.allDayDateRange ?? null,
    });
  }

  /**
   * The draft's current all-day date span — the authoritative range the
   * all-day-strip compose ghost renders. Falls back to the timed range's days
   * when no explicit span is set yet (the initial toggle from a timed drag).
   */
  get allDayDateRange(): Option<NaiveDate.Range> {
    const state = this.#state;
    if (!state || !this.#viewerTz) return null;
    return (
      state.allDayDateRange ??
      EventFields.timedRangeDays(state.time, this.#viewerTz)
    );
  }

  /** Category color of the draft (for the compose ghost's fill). */
  get selectedBackgroundColor(): Option<string> {
    const category = this.fields.selectedCategory();
    return category == null ? null : category.colorOpt() ?? "var(--gray)";
  }

  /**
   * Apply an all-day span edited from the all-day strip — the gesture->draft
   * channel. Writes ONLY the all-day representation (`allDayDateRange` + the
   * `isAllDay` selector); it NEVER touches the timed `time`, so toggling back to
   * timed still shows the original time. Refreshes the display for the active
   * representation and re-projects the ghost via `emitLiveUpdate`.
   */
  setAllDayDateRange(range: NaiveDate.Range): void {
    const state = this.#state;
    if (!state) return;
    state.allDayDateRange = range;
    state.isAllDay = true;
    this.$content.bindTime(EventFields.selectedEventTime(state));
    this.#emitLiveUpdate();
  }

  bind(
    time: DateTime.Range<Utc>,
    tz: TimezoneRegion,
    calendar: AuthScopedCalendarDisplay,
    options: Option<{
      isAllDay?: boolean;
      allDayDateRange?: NaiveDate.Range;
    }>,
  ): this {
    this.#dismissing = false;
    this.fields.resetCategoryUserSet();
    this.#state = {
      ...EventFields.draftState({ acal: calendar, time, isAllDay: options?.isAllDay ?? false, startTz: tz }),
      allDayDateRange: options?.allDayDateRange ?? null,
    };
    this.#viewerTz = tz;

    const rebind = () => {
      this.fields.bind(
        this.#draft,
        "event-details",
        tz,
        this.#boundCalendars.peekAll(),
        this.#categories,
      );
    };

    rebind();
    this.$content.bindTime(EventFields.selectedEventTime(this.#draft));

    this.$icon
      .bindDelegate({
        categories: this.#categories,
        onSelectCategory: (category: Option<EventCategoryItem>): void => {
          this.fields.setUserCategory(category);
          this.$icon.category = category;
          this.#emitLiveUpdate();
        },
        onSelectCalendar: (acal) => {
          if (this.#state) this.#state.acal = acal;
          this.fields.$fields.$calendar.$content.bind(acal);
          this.$icon.selectedCal = AuthScopedCalendarId.of(acal);
        },
        getCalendars: (): readonly Network.CalendarList[] => {
          return this.#boundCalendars.peekAll();
        },
        calendarWritability: this.#calendarWritability,
      })
      .bind({
        category: null,
        selectedCal: AuthScopedCalendarId.of(calendar),
      });

    const prefs = nonnull(
      this.#calendarPreferences,
      "CreateEventForm.bind() called before bindDependency()",
    );
    this.$content.bind({
      title: null,
      time: DateTimeRegion.Range.fromSingleTz(time, tz),
      use24Hour: prefs.peek().timeFormat === "24h",
      dateFormat: prefs.peek().dateFormat,
      onUpdateTime: (eventTime: EventTime) => {
        const state = this.#draft;
        const wasAllDay = state.isAllDay;
        state.isAllDay = eventTime.isAllDay;
        if (eventTime.isAllDay) {
          // Modal edited the all-day span — update ONLY the all-day
          // representation (the modal's end date is exclusive; store inclusive);
          // keep the timed `time` so toggling back shows the original time.
          state.allDayDateRange = new NaiveDate.Range(
            eventTime.range.start.ndt.date,
            eventTime.range.end.ndt.date.addDays(-1),
          );
        } else {
          // Modal edited the timed range — update ONLY the timed representation;
          // keep `allDayDateRange` so toggling to all-day shows its own span.
          state.time = new DateTime.Range(
            eventTime.range.start.asWallClock(),
            eventTime.range.end.asWallClock(),
          ).toUtc();
          state.startTz = eventTime.range.start.tz;
          state.endTz = eventTime.range.end.tz;
        }
        this.#emitLiveUpdate();

        if (wasAllDay !== eventTime.isAllDay) {
          // Keep the All-day toggle switch in sync with the date/time modal's
          // own all-day change.
          this.fields.$fields.$allDay.$content.$switch.setChecked(
            eventTime.isAllDay,
          );
        }
      },
      cta: {
        title: "Create Event",
        onAction: async () => await this.createEvent(),
      },
      editable: true,
      onUpdateTitle: (t) => {
        this.#draft.title = t ?? null;
        // Live category suggestion from the in-browser classifier. The
        // suggestion slot updates even when a manual category is displayed.
        const item = this.#categories.itemOpt(
          this.#sdkEvents?.classifier.suggestOpt(t ?? "", null) ?? null,
        );
        if (this.fields.setSuggestedCategory(item)) {
          this.$icon.category = this.fields.selectedCategory();
        }
        this.#emitLiveUpdate();
      },
      modalContext: "event-details",
    });
    this.$content.$title.controller.onEnterKey = async () =>
      await this.createEvent();

    return this;
  }

  bindTime(time: DateTime.Range<Utc>) {
    this.#state = {
      ...this.#draft,
      time,
    };
    this.fields.bind(
      this.#state,
      "event-details",
      nonnull(this.#viewerTz, "CreateEventForm: viewer zone read before bind"),
      this.#boundCalendars.peekAll(),
      this.#categories,
    );
    this.$content.bindTime(
      new EventTime(EventFields.dtrr(this.#draft), this.#draft.isAllDay),
    );
  }

  async createEvent() {
    if (this.#dismissing || !this.#state) return;
    await EventFields.createEvent(
      this.#state,
      nonnull(
        this.#sdkEvents,
        "CreateEventForm.createEvent() called before bindDependency()",
      ),
      this.#reportCreateRefused ?? EventWriteRefusal.toastForCalendar,
    );
    this.#dismissing = true;
    this.onDismiss();
  }

  static $styles: Style.Compat[] = [
    Button.$style,
    css`
      :host {
        width: 100%;
      }

      #content {
        width: 100%;
      }

      #description {
        cursor: text;
      }

      *:focus {
        outline: none;
      }

      #more-options {
        margin: 0px 4px;
        margin-bottom: 0px;
        align-self: center;
        border-radius: 12px;
        display: flex;
        flex-direction: row;
        align-items: center;
      }

      #more-options p {
        --font-size: var(--text-8px);
      }

      #more-options-icon {
        width: 10px;
        height: 10px;
        margin-left: 4px;
      }

      #more-options-icon svg {
        width: 100%;
        height: 100%;
        transform: rotate(90deg);
        transition: transform 0.5s ease;
      }

      :host([properties-expanded]) #more-options-icon svg {
        transform: rotate(-90deg);
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <std-event-details-content id="content">
      <lona-event-icon id=icon slot=icon style=margin-right:16px;></lona-event-icon>
      <std-event-details-field-container id=properties></std-event-details-field-container>
      <button id=more-options secondary><p id=more-options-label>More Options</p><std-row id="more-options-icon"></std-row></button>
    </std-event-details-content>
  `;
}

/** Types that go with `CreateEventForm`: `LiveUpdate`. */
export namespace CreateEventForm {
  export type LiveUpdate = {
    title: Option<string>;
    time: DateTime.Range<Utc>;
    category: Option<EventCategoryItem>;
    backgroundColor: Option<string>;
    isAllDay: boolean;
    allDayDateRange: Option<NaiveDate.Range>;
  };
}
